/* HomePage.js */
import React, { useState, useEffect, useRef } from "react";
import matchReference from "../utils/matchReference";
import MarginChart from "../utils/MarginChart";
import styles from "./HomePage.module.css";
import teamLogos from "../utils/teamLogos";
import TEAM_INFO from "../utils/teamInfo";
import { FaPencilAlt } from "react-icons/fa";
import teamFunFacts from "../utils/teamFunFacts.json";
import {
  format,
  differenceInSeconds,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
} from "date-fns";

// returns "1st", "2nd", "3rd", "4th", etc.
const formatOrdinal = (num) => {
  const j = num % 10,
    k = num % 100;
  if (j === 1 && k !== 11) return `${num}st`;
  if (j === 2 && k !== 12) return `${num}nd`;
  if (j === 3 && k !== 13) return `${num}rd`;
  return `${num}th`;
};

// returns remaining time until date, or null if passed
const calculateRemainingTime = (date) => {
  const now = new Date();
  const gameDate = new Date(date);
  const seconds = differenceInSeconds(gameDate, now);
  if (seconds <= 0) return null;

  const days = differenceInDays(gameDate, now);
  const hours = differenceInHours(gameDate, now) % 24;
  const minutes = differenceInMinutes(gameDate, now) % 60;

  if (days >= 1) return `In ${days}d ${hours}h`;
  if (hours >= 1) return `In ${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const HomePage = () => {
  // Hooks
  const [teamAbbrev, setTeamAbbrev] = useState(null);
  const teamAbbrevRef = useRef(null);
  const [showTeamSelect, setShowTeamSelect] = useState(false);
  const [stats, setStats] = useState({
    rank: null,
    pts: null,
    percentage: null,
    wins: null,
    losses: null,
    draws: null,
    played: null,
    for: null,
    against: null,
  });
  const [matchHistory, setMatchHistory] = useState(Array(10).fill("empty"));
  const [lastGame, setLastGame] = useState(null);
  const [currentGame, setCurrentGame] = useState(null);
  const [cumulativeData, setCumulativeData] = useState([]);
  const refreshInterval = useRef(null);
  const [roundsHistory, setRoundsHistory] = useState(Array(10).fill(null));
  const [matchHistoryGames, setMatchHistoryGames] = useState([]);
  const [dailyFact, setDailyFact] = useState("");
  const [standingsMap, setStandingsMap] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!teamAbbrev) return;

    const team = teamFunFacts[teamAbbrev];
    if (!team || !team.facts || team.facts.length === 0) {
      setDailyFact("No fun fact available.");
      return;
    }

    const factIndex = Math.floor(Math.random() * team.facts.length);

    setDailyFact(team.facts[factIndex]);
  }, [teamAbbrev]);

  const refreshData = (teamKey) => {
    if (!teamKey || teamKey === "no-team") return;

    const info = TEAM_INFO[teamKey];
    if (!info?.id) return;

    // Refresh standings
    fetch("https://api.squiggle.com.au/?q=standings")
      .then((r) => r.json())
      .then(({ standings }) => {
        const map = standings.reduce((acc, s) => {
          acc[s.id] = s;
          return acc;
        }, {});
        setStandingsMap(map);

        const row = map[info.id] ?? {};
        const wins = row?.wins ?? 0;
        const losses = row?.losses ?? 0;
        const draws = row?.draws ?? 0;

        setStats({
          rank: row?.rank ?? null,
          pts: row?.pts ?? null,
          percentage: row?.percentage ?? null,
          wins,
          losses,
          draws,
          played: wins + losses + draws,
          for: row?.for ?? null,
          against: row?.against ?? null,
        });
      })
      .catch(() => {
        setStats((s) => ({ ...s, wins: null, losses: null, draws: null }));
      });

    // Refresh games (excluding cumulativeData)
    const year = new Date().getFullYear();
    fetch(`https://api.squiggle.com.au/?q=games;year=${year};team=${info.id}`)
      .then((r) => r.json())
      .then(({ games }) => {
        const sortByDateAsc = (a, b) => new Date(a.date) - new Date(b.date);

        const completed = games
          .filter((g) => g.complete === 100)
          .sort(sortByDateAsc);
        setLastGame(completed.at(-1) ?? null);

        const upcoming = games
          .filter((g) => g.complete !== 100)
          .sort(sortByDateAsc);
        setCurrentGame(upcoming[0] ?? null);

        const rawRounds = completed
          .slice(-10)
          .map((g) => shortRound(g.roundname));
        const paddedRounds = [
          ...Array(10 - rawRounds.length).fill(null),
          ...rawRounds,
        ];
        setRoundsHistory(paddedRounds);

        const historyResults = completed
          .slice(-10)
          .map((g) =>
            g.winnerteamid === info.id
              ? "win"
              : g.winnerteamid
              ? "loss"
              : "draw"
          );

        const padding = Array(10 - historyResults.length).fill("empty");
        setMatchHistory([...padding, ...historyResults]);
      })
      .catch(() => {
        setMatchHistory(Array(10).fill("empty"));
        setLastGame(null);
        setCurrentGame(null);
      });
  };

  const resetRefreshTimer = () => {
    if (refreshInterval.current) clearInterval(refreshInterval.current);

    refreshInterval.current = setInterval(() => {
      if (document.visibilityState === "visible") {
        console.log("[INTERVAL] Refreshing home page data...");
        refreshData(teamAbbrevRef.current);
      } else {
        console.log("[INTERVAL] Skipped — tab not visible");
      }
    }, 60000); // 60 seconds
  };

  const getCurrentGameTitle = (game) => {
    if (!game) return "Next Match";

    const gameDate = new Date(game.date);
    const now = new Date();

    const day = now.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    const daysSinceTuesday = (day + 7 - 2) % 7; // 2 = Tuesday
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - daysSinceTuesday);
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6); // Tuesday + 6 = Monday
    weekEnd.setHours(23, 59, 59, 999);

    return gameDate >= weekStart && gameDate <= weekEnd && game.complete !== 100
      ? "This Week"
      : "Next Match";
  };

  // Helper to shorten round names:
  const shortRound = (name) => {
    if (!name) return "";
    const num = name.match(/\d+/);
    if (name.toLowerCase().includes("grand final")) return "GF";
    if (name.toLowerCase().includes("opening round")) return "OR";
    if (num) return `R${num[0]}`;
    return name;
  };

  useEffect(() => {
    resetRefreshTimer(); // Start interval on mount

    const events = ["click", "keydown", "mousemove", "touchstart", "scroll"];
    events.forEach((ev) => document.addEventListener(ev, resetRefreshTimer));

    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
      events.forEach((ev) =>
        document.removeEventListener(ev, resetRefreshTimer)
      );
    };
  }, []);

  const getAbbrevById = (id) =>
    Object.keys(TEAM_INFO).find((abbr) => TEAM_INFO[abbr].id === id);

  // 1) load selected team
  useEffect(() => {
    const saved = localStorage.getItem("selectedTeam");
    setTeamAbbrev(saved);
    teamAbbrevRef.current = saved;
  }, []);

  // click-outside ref
  const wrapperRef = useRef(null);

  // close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showTeamSelect &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setShowTeamSelect(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTeamSelect]);

  // handle pencil icon click
  const handleTeamClick = () => {
    setShowTeamSelect((v) => !v);
  };

  // handle dropdown change
  // eslint-disable-next-line no-unused-vars
  const handleTeamSelect = (e) => {
    const team = e.target.value;
    localStorage.setItem("selectedTeam", team);
    setTeamAbbrev(team);
    teamAbbrevRef.current = team;
    setCumulativeData([]);
    setShowTeamSelect(false);
  };

  // handle team pick
  const handleTeamPick = (team) => {
    localStorage.setItem("selectedTeam", team);
    setTeamAbbrev(team);
    teamAbbrevRef.current = team;
    setCumulativeData([]);
    setShowTeamSelect(false);
  };

  // 2) fetch standings
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!teamAbbrev || teamAbbrev === "no-team") {
      setStats({
        rank: null,
        pts: null,
        percentage: null,
        wins: null,
        losses: null,
        draws: null,
        played: null,
        for: null,
        against: null,
      });
      setStandingsMap({});
      return;
    }
    const info = TEAM_INFO[teamAbbrev];
    if (!info?.id) return;

    fetch("https://api.squiggle.com.au/?q=standings")
      .then((r) => r.json())
      .then(({ standings }) => {
        const map = standings.reduce((acc, s) => {
          acc[s.id] = s;
          return acc;
        }, {});
        setStandingsMap(map);

        const row = map[info.id] ?? {};
        const wins = row?.wins ?? 0;
        const losses = row?.losses ?? 0;
        const draws = row?.draws ?? 0;

        setStats({
          rank: row?.rank ?? null,
          pts: row?.pts ?? null,
          percentage: row?.percentage ?? null,
          wins,
          losses,
          draws,
          played: wins + losses + draws,
          for: row?.for ?? null,
          against: row?.against ?? null,
        });
      })
      .catch(() => {
        setStats((s) => ({ ...s, wins: null, losses: null, draws: null }));
        setStandingsMap({});
      });
  }, [teamAbbrev]);

  // 3) fetch games → history, lastGame, currentGame
  useEffect(() => {
    if (!teamAbbrev || teamAbbrev === "no-team") {
      setMatchHistory(Array(10).fill("empty"));
      setLastGame(null);
      setCurrentGame(null);
      setCumulativeData([]);
      return;
    }
    const info = TEAM_INFO[teamAbbrev];
    if (!info?.id) {
      setMatchHistory(Array(10).fill("empty"));
      setLastGame(null);
      setCurrentGame(null);
      return;
    }

    const year = new Date().getFullYear();
    fetch(`https://api.squiggle.com.au/?q=games;year=${year};team=${info.id}`)
      .then((r) => r.json())
      .then(({ games }) => {
        const sortByDateAsc = (a, b) => new Date(a.date) - new Date(b.date);

        const completed = games
          .filter((g) => g.complete === 100)
          .sort(sortByDateAsc);
        setLastGame(completed.at(-1) ?? null);

        const upcoming = games
          .filter((g) => g.complete !== 100)
          .sort(sortByDateAsc);
        setCurrentGame(upcoming[0] ?? null);

        // In your games‐fetching useEffect, after you compute historyResults:
        const rawRounds = completed
          .slice(-10)
          .map((g) => shortRound(g.roundname));
        const paddedRounds = [
          ...Array(10 - rawRounds.length).fill(null),
          ...rawRounds,
        ];
        setRoundsHistory(paddedRounds);

        const historyResults = completed
          .slice(-10)
          .map((g) =>
            g.winnerteamid === info.id
              ? "win"
              : g.winnerteamid
              ? "loss"
              : "draw"
          );

        const cumulativeData = [];
        completed.forEach((game, i) => {
          const margin =
            game.hteamid === teamId
              ? game.hscore - game.ascore
              : game.ascore - game.hscore;
          const prev = i > 0 ? cumulativeData[i - 1].cumulative : 0;
          cumulativeData.push({
            round: game.roundname,
            cumulative: prev + margin,
          });
        });
        setCumulativeData(cumulativeData);

        const padding = Array(10 - historyResults.length).fill("empty");
        setMatchHistory([...padding, ...historyResults]);

        const rawGames = completed.slice(-10);
        const paddedGames = [
          ...Array(10 - rawGames.length).fill(null),
          ...rawGames,
        ];
        setMatchHistory([...padding, ...historyResults]);
        setMatchHistoryGames(paddedGames);
      })
      .catch(() => {
        setMatchHistory(Array(10).fill("empty"));
        setLastGame(null);
        setCurrentGame(null);
      });
  }, [teamAbbrev]);

  // lookup display info
  const info = teamAbbrev ? TEAM_INFO[teamAbbrev] : null;
  const logoUrl = info ? teamLogos[info.logoKey] : null;
  const displayName = info ? info.fullName : "No Preferred Team";
  const teamId = TEAM_INFO[teamAbbrev]?.id;

  // look up AFL match‐centre URL for lastGame
  const lastRef =
    lastGame && matchReference.find((r) => r.id === String(lastGame.id));
  const lastAflUrl = lastRef
    ? `https://www.afl.com.au/matches/${lastRef.aflid}`
    : null;

  // lastGame result CSS class
  const lastIsHome = lastGame?.hteamid === teamId;
  const lastTeamScore = lastIsHome ? lastGame?.hscore : lastGame?.ascore;
  const lastOppScore = lastIsHome ? lastGame?.ascore : lastGame?.hscore;
  const lastResultKey =
    lastTeamScore > lastOppScore
      ? "win"
      : lastTeamScore < lastOppScore
      ? "loss"
      : "draw";
  const lastResultClass = `${lastResultKey}Text`;

  // currentGame result CSS class & margin text
  const currIsHome = currentGame?.hteamid === teamId;
  const currTeamScore = currIsHome ? currentGame?.hscore : currentGame?.ascore;
  const currOppScore = currIsHome ? currentGame?.ascore : currentGame?.hscore;
  const currResultKey =
    currTeamScore > currOppScore
      ? "win"
      : currTeamScore < currOppScore
      ? "loss"
      : "draw";
  const currResultClass = `${currResultKey}Text`;

  return (
    <div className={styles.homePage}>
      <div className={styles.pageTopSpacer} />

      {/* Team header + dropdown wrapper */}
      <div className={styles.teamSelectContainer} ref={wrapperRef}>
        <div
          className={`${styles.teamHeader} ${
            showTeamSelect ? styles.teamHeaderActive : ""
          }`}
          onClick={handleTeamClick}
        >
          {logoUrl && (
            <img
              src={logoUrl}
              alt={`${displayName} logo`}
              className={styles.teamLogo}
            />
          )}
          <span className={styles.teamName}>{displayName}</span>

          {stats.rank != null && (
            <span className={styles.ladderInHeader}>
              {formatOrdinal(stats.rank)}
            </span>
          )}
          <span className={styles.editIcon}>
            <FaPencilAlt />
          </span>
        </div>

        {showTeamSelect && (
          <>
            <div
              className={styles.dropdownBackdrop}
              onClick={() => setShowTeamSelect(false)}
            />
            <div
              className={styles.teamSelectDropdown}
              onScroll={resetRefreshTimer}
            >
              <div
                className={`${styles.teamOption} ${
                  teamAbbrev === "no-team" ? styles.selectedOption : ""
                }`}
                onClick={() => handleTeamPick("no-team")}
              >
                <span>No Preferred Team</span>
              </div>
              {Object.entries(TEAM_INFO).map(([abbr, info]) => (
                <div
                  key={abbr}
                  className={`${styles.teamOption} ${
                    abbr === teamAbbrev ? styles.selectedOption : ""
                  }`}
                  onClick={() => handleTeamPick(abbr)}
                >
                  <img
                    src={teamLogos[info.logoKey]}
                    alt=""
                    className={styles.optionLogo}
                  />
                  <span>{info.fullName}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Team stats */}
      {/* 1st row: Played / Won / Lost / Drawn */}
      <div className={styles.statsGrid}>
        <div className={styles.statBox}>
          <span className={styles.statTitle}>Played</span>
          <span className={styles.statValue}>{stats.played ?? "-"}</span>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statTitle}>Won</span>
          <span className={styles.statValue}>{stats.wins ?? "-"}</span>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statTitle}>Lost</span>
          <span className={styles.statValue}>{stats.losses ?? "-"}</span>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statTitle}>Drawn</span>
          <span className={styles.statValue}>{stats.draws ?? "-"}</span>
        </div>
      </div>

      {/* 2nd row: Points / % / For / Against */}
      <div className={styles.statsGrid}>
        <div className={styles.statBox}>
          <span className={styles.statTitle}>Points</span>
          <span className={styles.statValue}>{stats.pts ?? "-"}</span>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statTitle}>%</span>
          <span className={styles.statValue}>
            {stats.percentage != null ? `${stats.percentage.toFixed(1)}` : "-"}
          </span>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statTitle}>PF</span>
          <span className={styles.statValue}>{stats.for ?? "-"}</span>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statTitle}>PA</span>
          <span className={styles.statValue}>{stats.against ?? "-"}</span>
        </div>
      </div>

      {/* in HomePage.js, inside your return(): */}

      {/* Match History */}
      <div className={styles.matchHistoryContainer}>
        <div className={styles.matchHistoryHeader}>
          <span className={styles.roundLabelLeft}>
            {roundsHistory[0] || ""}
          </span>
          <span className={styles.matchHistoryTitle}>Match History</span>
          <span className={styles.roundLabelRight}>
            {roundsHistory[9] || ""}
          </span>
        </div>

        <div className={styles.matchHistory}>
          {matchHistory.map((res, i) => {
            const letter =
              res === "win"
                ? "W"
                : res === "loss"
                ? "L"
                : res === "draw"
                ? "D"
                : "";
            const game = matchHistoryGames[i];
            const ref =
              game &&
              matchReference.find((r) => String(r.id) === String(game.id));

            return (
              <div key={i} className={styles.matchCircleWrapper}>
                <div
                  className={`${styles.matchCircle} ${styles[res + "Circle"]}`}
                >
                  {letter}
                </div>

                {game && ref && (
                  <div className={styles.tooltipContainer}>
                    <a
                      href={`https://www.afl.com.au/matches/${ref.aflid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${styles.thisWeekGame} ${
                        game.complete !== 100 ? styles.liveCard : ""
                      }`}
                    >
                      <span className={styles.thisWeekGameTitle}>
                        {game.roundname}
                      </span>
                      <div className={styles.gameDetailsList}>
                        <div className={styles.gameDetailsRow}>
                          <span className={styles.thisWeekHomeInfo}>
                            <img
                              src={
                                teamLogos[
                                  TEAM_INFO[getAbbrevById(game.hteamid)].logoKey
                                ]
                              }
                              alt=""
                              className={styles.smallLogo}
                            />
                            {getAbbrevById(game.hteamid)}
                          </span>
                          <span className={styles.thisWeekHomeScorePanel}>
                            {game.hgoals}.{game.hbehinds} ({game.hscore})
                          </span>
                        </div>

                        <div className={styles.gameDetailsRow}>
                          <span className={styles.thisWeekAwayInfo}>
                            <img
                              src={
                                teamLogos[
                                  TEAM_INFO[getAbbrevById(game.ateamid)].logoKey
                                ]
                              }
                              alt=""
                              className={styles.smallLogo}
                            />
                            {getAbbrevById(game.ateamid)}
                          </span>
                          <span className={styles.thisWeekAwayScorePanel}>
                            {game.agoals}.{game.abehinds} ({game.ascore})
                          </span>
                        </div>
                      </div>
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Games (Last & This Week) */}
      <div className={styles.gamesContainer}>
        {/* Last Match */}
        <a
          href={lastAflUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.lastWeekGame}
        >
          <span className={styles.lastWeekGameTitle}>Last Match</span>
          {lastGame == null ? (
            <span className={styles.loading}>–</span>
          ) : (
            <div className={styles.gameDetailsList}>
              <div className={styles.gameDetailsRow}>
                <span className={styles.lastWeekVenue}>{lastGame.venue}</span>
                <span className={styles.lastWeekRound}>
                  {lastGame.roundname}
                </span>
              </div>
              <div className={styles.gameDetailsRow}>
                <span className={styles.lastWeekHomeInfo}>
                  <img
                    src={
                      teamLogos[
                        TEAM_INFO[getAbbrevById(lastGame.hteamid)].logoKey
                      ]
                    }
                    alt={getAbbrevById(lastGame.hteamid)}
                    className={styles.smallLogo}
                  />
                  {getAbbrevById(lastGame.hteamid)}
                </span>
                <span
                  className={`${styles.lastWeekHomeScorePanel} ${
                    lastIsHome ? styles[lastResultClass] : ""
                  }`}
                >
                  {lastGame.hgoals}.{lastGame.hbehinds} ({lastGame.hscore})
                </span>
              </div>
              <div className={styles.gameDetailsRow}>
                <span className={styles.lastWeekAwayInfo}>
                  <img
                    src={
                      teamLogos[
                        TEAM_INFO[getAbbrevById(lastGame.ateamid)].logoKey
                      ]
                    }
                    alt={getAbbrevById(lastGame.ateamid)}
                    className={styles.smallLogo}
                  />
                  {getAbbrevById(lastGame.ateamid)}
                </span>
                <span
                  className={`${styles.lastWeekAwayScorePanel} ${
                    !lastIsHome ? styles[lastResultClass] : ""
                  }`}
                >
                  {lastGame.agoals}.{lastGame.abehinds} ({lastGame.ascore})
                </span>
              </div>
              <div className={styles.gameDetailsRow}>
                <span className={styles.lastWeekTime}>{lastGame.timestr}</span>
                <span className={styles.lastWeekMargin}>
                  {`${teamAbbrev.toUpperCase()} ${
                    lastResultKey === "win"
                      ? "won"
                      : lastResultKey === "loss"
                      ? "lost"
                      : "drew"
                  } by ${Math.abs(lastTeamScore - lastOppScore)}`}
                </span>
              </div>
            </div>
          )}
        </a>

        {/* This Week */}
        <a
          href={
            matchReference.find((r) => r.id === String(currentGame?.id))
              ? `https://www.afl.com.au/matches/${
                  matchReference.find((r) => r.id === String(currentGame.id))
                    .aflid
                }`
              : "#"
          }
          target="_blank"
          rel="noopener noreferrer"
          className={`${styles.thisWeekGame} ${
            currentGame?.complete !== 100 ? styles.liveCard : ""
          }`}
        >
          <span className={styles.thisWeekGameTitle}>
            {getCurrentGameTitle(currentGame)}
          </span>

          {currentGame == null ? (
            <span className={styles.loading}>–</span>
          ) : currentGame.timestr == null ? (
            <div className={styles.gameDetailsList}>
              <div className={styles.gameDetailsRow}>
                <span className={styles.thisWeekVenue}>
                  {currentGame.venue}
                </span>
                <span className={styles.thisWeekRound}>
                  {format(new Date(currentGame.date), "EEE MMM dd")}
                </span>
              </div>
              <div className={styles.gameDetailsRow}>
                <span className={styles.thisWeekHomeInfo}>
                  <img
                    src={
                      teamLogos[
                        TEAM_INFO[getAbbrevById(currentGame.hteamid)].logoKey
                      ]
                    }
                    alt={getAbbrevById(currentGame.hteamid)}
                    className={styles.smallLogo}
                  />
                  {getAbbrevById(currentGame.hteamid)}
                </span>
                <span className={styles.thisWeekHomeScorePanel}></span>
                <span className={styles.thisWeekHomeRank}>
                  {standingsMap[currentGame.hteamid]
                    ? formatOrdinal(standingsMap[currentGame.hteamid].rank)
                    : "-"}
                </span>
              </div>
              <div className={styles.gameDetailsRow}>
                <span className={styles.thisWeekAwayInfo}>
                  <img
                    src={
                      teamLogos[
                        TEAM_INFO[getAbbrevById(currentGame.ateamid)].logoKey
                      ]
                    }
                    alt={getAbbrevById(currentGame.ateamid)}
                    className={styles.smallLogo}
                  />
                  {getAbbrevById(currentGame.ateamid)}
                </span>
                <span className={styles.thisWeekAwayScorePanel}></span>
                <span className={styles.thisWeekAwayRank}>
                  {standingsMap[currentGame.ateamid]
                    ? formatOrdinal(standingsMap[currentGame.ateamid].rank)
                    : "-"}
                </span>
              </div>
              <div className={styles.gameDetailsRow}>
                <span className={styles.thisWeekTime}>
                  {calculateRemainingTime(currentGame.date)}
                </span>
                <span className={styles.thisWeekMargin}>
                  {format(new Date(currentGame.date), "h:mm a").toLowerCase()}
                </span>
              </div>
            </div>
          ) : (
            <div className={styles.gameDetailsList}>
              <div className={styles.gameDetailsRow}>
                <span className={styles.thisWeekVenue}>
                  {currentGame.venue}
                </span>
                <span className={styles.thisWeekRound}>
                  {currentGame.roundname}
                </span>
              </div>
              <div className={styles.gameDetailsRow}>
                <span className={styles.thisWeekHomeInfo}>
                  <img
                    src={
                      teamLogos[
                        TEAM_INFO[getAbbrevById(currentGame.hteamid)].logoKey
                      ]
                    }
                    alt={getAbbrevById(currentGame.hteamid)}
                    className={styles.smallLogo}
                  />
                  {getAbbrevById(currentGame.hteamid)}
                </span>
                <span
                  className={`${styles.thisWeekHomeScorePanel} ${
                    currIsHome ? styles[currResultClass] : ""
                  }`}
                >
                  {currentGame.hgoals}.{currentGame.hbehinds} (
                  {currentGame.hscore})
                </span>
              </div>
              <div className={styles.gameDetailsRow}>
                <span className={styles.thisWeekAwayInfo}>
                  <img
                    src={
                      teamLogos[
                        TEAM_INFO[getAbbrevById(currentGame.ateamid)].logoKey
                      ]
                    }
                    alt={getAbbrevById(currentGame.ateamid)}
                    className={styles.smallLogo}
                  />
                  {getAbbrevById(currentGame.ateamid)}
                </span>
                <span
                  className={`${styles.thisWeekAwayScorePanel} ${
                    !currIsHome ? styles[currResultClass] : ""
                  }`}
                >
                  {currentGame.abehinds}.{currentGame.abehinds} (
                  {currentGame.ascore})
                </span>
              </div>
              <div className={styles.gameDetailsRow}>
                <span className={styles.thisWeekTime}>
                  {currentGame.timestr}
                </span>
                <span className={styles.matchTime}>Live</span>
              </div>
            </div>
          )}
        </a>
      </div>

      <div className={styles.marginChartContainer}>
        <span className={styles.cumulativeMarginTitle}>Weekly Margin</span>
        <MarginChart data={cumulativeData} />
      </div>
      <div className={styles.funFactContainer}>
        <p className={styles.funFactText}>{dailyFact}</p>
      </div>
    </div>
  );
};

export default HomePage;
